/**
 * BrokerSettingsManager - 交易商设置管理器
 * 
 * 管理交易商的全局设置（费率、对冲等）
 */

import type { BrokerSettings } from '../types/brokerSettings.types';
import { DEFAULT_BROKER_SETTINGS } from '../types/brokerSettings.types';
import { storage } from '../utils/storage';
import { logger } from '../utils/logger';

const STORAGE_KEY_PREFIX = 'broker_settings_';

export class BrokerSettingsManager {
  private settingsCache: Map<string, BrokerSettings>;

  constructor() {
    this.settingsCache = new Map();
    logger.log('BrokerSettingsManager initialized');
  }

  /**
   * 获取交易商的全局设置
   */
  getSettings(brokerUid: string): BrokerSettings | null {
    // 先从缓存读取
    if (this.settingsCache.has(brokerUid)) {
      return this.settingsCache.get(brokerUid)!;
    }

    // 从存储加载
    const settings = storage.get<BrokerSettings>(`${STORAGE_KEY_PREFIX}${brokerUid}`);
    if (settings) {
      this.settingsCache.set(brokerUid, settings);
      return settings;
    }

    // 如果不存在，创建默认设置
    return this.createDefaultSettings(brokerUid, 'SYSTEM');
  }

  /**
   * 创建默认交易商设置
   */
  createDefaultSettings(brokerUid: string, updatedBy: string = 'SYSTEM'): BrokerSettings {
    const settings: BrokerSettings = {
      brokerUid,
      ...DEFAULT_BROKER_SETTINGS,
      updatedAt: Date.now(),
      updatedBy,
    };

    this.saveSettings(brokerUid, settings);
    logger.log(`✅ Created default broker settings for ${brokerUid}`);
    
    return settings;
  }

  /**
   * 保存交易商设置
   */
  saveSettings(brokerUid: string, settings: BrokerSettings): void {
    // 更新时间戳
    settings.updatedAt = Date.now();

    // 保存到缓存
    this.settingsCache.set(brokerUid, settings);

    // 保存到存储
    storage.set(`${STORAGE_KEY_PREFIX}${brokerUid}`, settings);

    logger.log(`💾 Saved broker settings for ${brokerUid}`);
  }

  /**
   * 更新交易商设置（部分更新）
   */
  updateSettings(
    brokerUid: string, 
    updates: Partial<Omit<BrokerSettings, 'brokerUid' | 'updatedAt'>>,
    updatedBy: string
  ): BrokerSettings | null {
    let currentSettings = this.getSettings(brokerUid);
    
    // 如果不存在，创建默认设置
    if (!currentSettings) {
      currentSettings = this.createDefaultSettings(brokerUid, updatedBy);
    }

    // 深度合并
    const updatedSettings: BrokerSettings = {
      ...currentSettings,
      ...updates,
      fees: { ...currentSettings.fees, ...updates.fees },
      rebate: { ...currentSettings.rebate, ...updates.rebate },
      hedge: { ...currentSettings.hedge, ...updates.hedge },
      updatedAt: Date.now(),
      updatedBy,
    };

    this.saveSettings(brokerUid, updatedSettings);
    return updatedSettings;
  }

  /**
   * 获取交易商的 Taker 费率
   */
  getTakerFeeRate(brokerUid: string): number {
    const settings = this.getSettings(brokerUid);
    return settings?.fees.taker || DEFAULT_BROKER_SETTINGS.fees.taker;
  }

  /**
   * 获取交易商的 Maker 费率
   */
  getMakerFeeRate(brokerUid: string): number {
    const settings = this.getSettings(brokerUid);
    return settings?.fees.maker || DEFAULT_BROKER_SETTINGS.fees.maker;
  }

  /**
   * 获取交易商回馈比例
   */
  getRebateRate(brokerUid: string): number {
    const settings = this.getSettings(brokerUid);
    if (!settings?.rebate.enabled) {
      return 0;
    }
    return settings.rebate.rate || 0;
  }

  /**
   * 删除交易商设置
   */
  deleteSettings(brokerUid: string): void {
    this.settingsCache.delete(brokerUid);
    storage.remove(`${STORAGE_KEY_PREFIX}${brokerUid}`);
    logger.log(`🗑️ Deleted broker settings for ${brokerUid}`);
  }

  /**
   * 获取所有交易商设置
   */
  getAllSettings(): BrokerSettings[] {
    const allSettings: BrokerSettings[] = [];
    
    // 从 storage 加载所有
    const keys = storage.keys();
    for (const key of keys) {
      if (key.startsWith(STORAGE_KEY_PREFIX)) {
        const settings = storage.get<BrokerSettings>(key);
        if (settings) {
          allSettings.push(settings);
          this.settingsCache.set(settings.brokerUid, settings);
        }
      }
    }

    return allSettings;
  }
}

